import { ComponentStep } from './../product-wizard/product-wizard-step-factory.service';
import { Content, ContentDetailed, EditContent } from 'app/shared/models/content';
import { ProductTier } from 'app/shared/models/product-tier.model';
import { SelectedGroup } from 'app/shared/models/group/group.model';

export interface ProductDefinition {
  id: number;
  key: string;
  name: string;
  name2: Content;
  title: string;
  title2: Content;
  description: string;
  description2: Content;
  highlight: string;
  highlight2: Content;
  icon?: string;
  baseName: string;
  definition: string;
  category: ProductCategory;
  productType: ProductType;
  checked: boolean;
  definitionObject: ProductDefinitionJson;
  deletable: boolean;
  isActive: boolean;
}

export interface CreateProductDefinition {
  key: string;
  name: ContentDetailed;
  title: ContentDetailed;
  description: ContentDetailed;
  highlight: ContentDetailed;
  definition: string;
  category: ProductCategory;
  productType: ProductType;
  isActive: boolean;
  productTiers?: ProductTier[];
  paymentSettings?: ProductPaymentSettings;
}

export interface UpdateProductDefinition extends CreateProductDefinition {
  id: number;
}

export interface ProductPaymentSettings {
  paymentProductId?: string;
  purchasable: boolean;
  availableProductContexts: string[];
  termsVersion?: string;
  /** The order confirmation email per subscription event; unset means nothing is sent for that event. */
  confirmationEmails?: PaymentConfirmationEmails;
}

export interface PaymentConfirmationEmails {
  subscriptionCreated?: number | null;
  /** A higher plan on the same billing period, invoiced now. */
  planUpgraded?: number | null;
  /** A lower plan or another billing period, from the end of the paid period. */
  planChangeScheduled?: number | null;
  planChangeCancelled?: number | null;
  subscriptionCancelled?: number | null;
  subscriptionRenewed?: number | null;
}

export interface UpdateCompanyProductDefinition {
  id: number;
  name: EditContent;
  nameText?: string;
  baseProductNameText?: string;
  title: EditContent;
  description: EditContent;
  highlight: EditContent;
  definition: string;
  category: ProductCategory;
  productType: ProductType;
  checked: boolean;
  definitionObject: ProductDefinitionJson;
  deletable: boolean;
  isActive: boolean;
  productTierId?: number;
  productTiers?: ProductTier[];
  productContextId?: number;
  productContextKey?: string;
  enabledForAll?: boolean;
  enabledGroups?: SelectedGroup[];
}

export interface CompanyProductDefinition {
  id: number;
  name: string;
  key: string;
  description: string;
  baseName: string;
  definition: string;
  definitionObject: ProductDefinitionJson;
  category: ProductCategory;
  productType: ProductType;
  checked: boolean;
  highlight: string;
  totalQuestions: number;
}

export interface ProductDefinitionPrerequisites {
  entityType: string;
  template: any;
  output?: string;
}

export interface ProductDefinitionJson {
  editMode: boolean;
  productName: string;
  description: string;
  images?: ProductImageSettings;
  defaultCommunicationModuleKeys?: ProductDefaultCommunicationModuleKeys;
  reporting?: ProductReportingSettings;
  config: ProductDefinitionConfig[];
  summary: string;
  steps: ProductDefinitionJsonStep[] | SurveyProductDefinitionJsonStep[];
  prerequisites: ProductDefinitionPrerequisites[];
  survey?: SurveyProductSettings;
  followup?: FollowupProductSettings;
  order: number;
  integratedStepper: boolean;
  allowMultiple?: boolean;
}

export type OrgHierarchyType =
  | 'Organisation'
  | 'Matrix'
  | 'ThreeSixty'
  | 'Managers'
  | 'Background'
  | 'Segments';

export enum CommunicationMethod {
  Email = 'Email',
  Sms = 'Sms',
}

export type MultipleSessionsType = 'NotAllowed' | 'ByGroup' | 'BySurveyRun';

export interface SurveyProductSettings {
  anonymity?: any;
  availableIndexTags?: string[];
  requiredIndexKeys?: string[];
  requiredIndexIds?: number[];
  allowedHierarchyTypes?: OrgHierarchyType[];
  multipleSessions?: MultipleSessionsType;
  ignoreExcludedFlag?: boolean;
  singleInstance: boolean;
  separateByGroup: boolean;
  showPerspectives?: boolean;
  validateIndexes?: boolean;
  communicationMethods?: CommunicationMethod[];
  autoReportSettings?: AutoReportSettings;
  followupPulse?: FollowupPulse;
  recurring?: boolean;
  schedules?: string[];
  duration?: string;
}

export interface ProductImageSettings {
  background: string;
  icon: string;
}

export interface FollowupProductSettings {
  metaTypes?: string[];
}

export interface FollowupPulse {
  minQuestions?: number;
  maxQuestions?: number;
}

export interface ProductDefinitionJsonStep {
  stepId: ComponentStep;
  show: boolean;
  showInEditMode?: boolean;
  preselected?: string[];
  settings?: any;
}

export interface ProductDefinitionConfig {
  key: string;
  value: string;
}

export interface SurveyProductDefinitionJsonStep extends ProductDefinitionJsonStep {
  availableModuleTags?: string[];
  availableModuleKeys?: string[];
  preselected?: string[];
  main?: string;
  canAddReminders?: boolean;
  scheduleSelection?: 'once' | 'weekly' | 'monthly' | 'quarterly';
  activationTimeSelection?: boolean;
}

export interface DropdownModel<T = string> {
  key: T | undefined;
  name: string;
}

export interface ProductDefinitionSurveyJson extends ProductDefinitionJson {
  steps: SurveyProductDefinitionJsonStep[];
}

export enum ProductCategory {
  EX = 'EX',
  CX = 'CX',
}

export enum Tier {
  Essential = 'Essential',
  Premium = 'Premium',
}

export enum ProductType {
  Survey = 'Survey',
  Followup = 'Followup',
  Report = 'Report',
  CompanySetup = 'CompanySetup',
}

export interface EditCompanyProductDefinition {
  companyId: number;
  productDefinitionId?: number;
  definition: string;
  name: EditContent;
  title: EditContent;
  highlight: EditContent;
  description: EditContent;
  isActive: boolean;
  enabledForAll?: boolean;
  enabledGroups?: SelectedGroup[];
  productContextKey?: string;
}

export interface AutoReportSettings {
  mode: AutoReportMode;
  groupCategory?: string;
  includeUsers?: boolean;
  releases?: ReportRelease[];
}

export interface ReportRelease {
  mode: AutoReportMode;
  groupCategory?: string;
  emailId?: number;
  smsId?: number;
  delayHours?: number;
  visibility?: number;
}

export interface ProductDefaultCommunicationModuleKeys {
  activation?: string;
  report?: string;
  welcome?: string;
}

export interface ProductReportingSettings {
  includeUsersInReport?: boolean;
}

export enum AutoReportMode {
  GroupManagers = 'GroupManagers',
  GroupCategory = 'GroupCategory',
  GroupManagersAndUsers = 'GroupManagersAndUsers',
}
